import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, CheckCircle, WarningCircle, FilePdf } from "@phosphor-icons/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api, formatApiErrorDetail } from "../context/AuthContext";

const CATEGORIES = ["ristoranti", "pizzerie", "bar", "gelaterie", "pasticcerie", "panetterie", "farmacie",
  "supermercati", "abbigliamento", "parrucchieri", "estetiste", "hotel", "palestre", "ferramenta",
  "fiorai", "macellerie", "tabaccherie", "banche"];
const RADII = [300, 500, 1000, 2000];

const SourceBadge = ({ status }) => {
  if (!status) return null;
  const entries = Object.entries(status).map(([name, st]) => {
    const label = name === "overpass" ? "OpenStreetMap" : "Foursquare";
    if (st === "ok") return { label, ok: true, text: `${label}: attiva` };
    if (st === "key_mancante") return { label, ok: false, text: `${label}: in attesa della chiave API` };
    const soft = String(st).startsWith("errore") ? "momentaneamente non disponibile" : st;
    return { label, ok: false, text: `${label}: ${soft}` };
  });
  return (
    <div className="flex flex-wrap gap-3 mt-2" data-testid="market-sources-status">
      {entries.map((e) => (
        <span key={e.label} className={`text-[11px] font-semibold flex items-center gap-1 ${e.ok ? "text-emerald-400" : "text-amber-400"}`}>
          {e.ok ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />} {e.text}
        </span>
      ))}
    </div>
  );
};

const MarketMap = ({ data }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!data || !ref.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const map = L.map(ref.current).setView([data.center.lat, data.center.lon], 15);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    L.circle([data.center.lat, data.center.lon], { radius: data.radius, color: "#45E0D0", weight: 1.5, fillOpacity: 0.05 }).addTo(map);
    L.circleMarker([data.center.lat, data.center.lon], { radius: 8, color: "#45E0D0", fillColor: "#45E0D0", fillOpacity: 0.9 })
      .addTo(map).bindPopup("Punto di ricerca");
    const bounds = [[data.center.lat, data.center.lon]];
    data.results.forEach((r) => {
      L.circleMarker([r.lat, r.lon], { radius: 6, color: "#E9A23B", fillColor: "#E9A23B", fillOpacity: 0.85, weight: 1 })
        .addTo(map)
        .bindPopup(`<b>${r.name}</b><br/>${r.category}<br/>${r.address || ""}<br/>${r.distance_m} m · ${r.source}`);
      bounds.push([r.lat, r.lon]);
    });
    if (bounds.length > 1) map.fitBounds(bounds, { padding: [30, 30] });
    return () => { map.remove(); mapRef.current = null; };
  }, [data]);

  if (!data) return null;
  return <div ref={ref} data-testid="market-map" className="w-full rounded-lg border border-white/10 overflow-hidden" style={{ height: 420, background: "#111820" }} />;
};

export const MarketResearch = () => {
  const [address, setAddress] = useState("");
  const [category, setCategory] = useState("ristoranti");
  const [radius, setRadius] = useState(500);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [data, setData] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!address.trim()) return toast.error("Inserisci un indirizzo o una zona");
    setLoading(true);
    try {
      const { data: res } = await api.post("/market/search", { address, category, radius });
      setData(res);
      if (res.cached) toast.info("Risultati dalla cache (aggiornati nelle ultime 24h)");
      else toast.success(`${res.count} attività trovate`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ricerca non riuscita");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!data) return;
    setReportLoading(true);
    try {
      const { data: rep } = await api.post("/market/report", {
        address: data.address, category: data.category, radius: data.radius,
      });
      const res = await api.get(`/viki/documents/${rep.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = rep.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report PDF scaricato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Report non generato");
    } finally {
      setReportLoading(false);
    }
  };

  const inputCls = "h-11 bg-transparent border-white/15 text-white focus:border-white/40";
  return (
    <section data-testid="market-page">
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Analisi geografica della concorrenza: inserisci un indirizzo o una zona e una categoria — VIKI incrocia
        i vari dati e ti mostra le attività presenti nel raggio scelto, in tabella e su mappa.
      </p>

      <form onSubmit={search} className="p-6 rounded-lg bg-surface border border-surface-border flex flex-wrap items-end gap-4 mb-8">
        <div className="flex-1 min-w-[260px] space-y-1.5">
          <label className="text-[10px] tracking-[0.2em] uppercase text-white/50">Indirizzo / via / zona</label>
          <Input data-testid="market-address-input" value={address} onChange={(e) => setAddress(e.target.value)}
            placeholder="es. Via XX Settembre, Bergamo" className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] tracking-[0.2em] uppercase text-white/50">Categoria</label>
          <div className="flex gap-2">
            <select data-testid="market-category-select" value={CATEGORIES.includes(category) ? category : "__custom"}
              onChange={(e) => e.target.value !== "__custom" && setCategory(e.target.value)}
              className="h-11 rounded-md bg-[#161F29] border border-white/15 text-white text-sm px-3">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__custom">altra…</option>
            </select>
            <Input data-testid="market-category-input" value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="categoria libera" className={`${inputCls} w-40`} />
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] tracking-[0.2em] uppercase text-white/50">Raggio</label>
          <select data-testid="market-radius-select" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="h-11 rounded-md bg-[#161F29] border border-white/15 text-white text-sm px-3">
            {RADII.map((r) => <option key={r} value={r}>{r >= 1000 ? `${r / 1000} km` : `${r} m`}</option>)}
          </select>
        </div>
        <Button type="submit" disabled={loading} data-testid="market-search-button"
          className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
          <MagnifyingGlass size={15} className="mr-1.5" /> {loading ? "Analizzo la zona..." : "Cerca"}
        </Button>
      </form>

      {data && (
        <>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm text-white/80" data-testid="market-summary">
                <b>{data.count}</b> attività "{data.category}" entro <b>{data.radius} m</b> da {data.center.display_name}
                {data.cached && <span className="text-white/40"> · cache 24h</span>}
                {typeof data.foursquare_remaining === "number" && (
                  <span className={data.foursquare_remaining < 100 ? "text-amber-400" : "text-white/40"}>
                    {" "}· Foursquare: {data.foursquare_remaining} chiamate residue questo mese
                    {data.foursquare_remaining < 100 ? " ⚠ vicino al limite" : ""}
                  </span>
                )}
              </p>
              <SourceBadge status={data.sources_status} />
            </div>
            {data.count > 0 && (
              <Button onClick={downloadReport} disabled={reportLoading} data-testid="market-report-button"
                className="h-10 px-5 bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold text-sm shrink-0">
                <FilePdf size={16} className="mr-1.5" weight="fill" />
                {reportLoading ? "VIKI scrive il report..." : "Report PDF"}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MarketMap data={data} />
            <div className="rounded-lg bg-surface border border-surface-border overflow-auto" style={{ maxHeight: 420 }}>
              <table className="w-full text-sm" data-testid="market-results-table">
                <thead className="sticky top-0 bg-[#161F29]">
                  <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-white/40">
                    <th className="px-4 py-3">Nome</th><th className="px-3 py-3">Categoria</th>
                    <th className="px-3 py-3">Indirizzo</th><th className="px-3 py-3">Dist.</th><th className="px-3 py-3">Fonte</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r, i) => (
                    <tr key={i} className="border-t border-white/5 text-white/80">
                      <td className="px-4 py-2.5 font-medium text-white/90">{r.name}</td>
                      <td className="px-3 py-2.5 text-white/60">{r.category}</td>
                      <td className="px-3 py-2.5 text-white/60">{r.address || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.distance_m} m</td>
                      <td className="px-3 py-2.5 text-[11px] text-white/45">{r.source}</td>
                    </tr>
                  ))}
                  {!data.results.length && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">Nessuna attività trovata in questo raggio.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
